import type { Lead, Visit } from '../../../shared/types';

/** CSV export (M5, D37) — RFC 4180: CRLF rows, fields quoted when they contain comma, quote or
 *  newline, quotes doubled. Headers are human/Salesforce-import friendly. Pure + on-device. */

/** A text cell starting with = (or a tab / CR before it) is run as a formula by Excel, Sheets and Numbers,
 *  and one starting with + - @ is too when it carries a function call or a DDE pipe: a name from a
 *  public filing could carry one (CSV injection). The OWASP guard, a leading apostrophe inside quotes,
 *  goes on those only — a phone (+1 714…) or an Instagram handle (@sunsetnails) stays as typed, since
 *  the Salesforce import would keep the apostrophe as data. Numbers are never touched. */
const FORMULA = /^[=\t\r]|^[+\-@][\s\S]*[(|!]/;
const guardFormula = (value: string | number, s: string): string =>
  typeof value === 'string' && FORMULA.test(s) ? `'${s}` : s;

export const csvField = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  const guarded = guardFormula(value, s);
  return guarded !== s || /[",\r\n]/.test(s) ? `"${guarded.replaceAll('"', '""')}"` : s;
};

export const buildCsv = (
  headers: readonly string[],
  rows: readonly (readonly (string | number | null)[])[],
): string =>
  [headers.map(csvField).join(','), ...rows.map((r) => r.map(csvField).join(','))].join('\r\n') + '\r\n';

/** Excel decodes a CSV as the locale's legacy code page unless it starts with a byte-order mark;
 *  Numbers, Sheets and the Salesforce import (set to UTF-8) read it either way. */
const BOM = '\uFEFF';

const LEAD_COLUMNS: readonly [string, (l: Lead) => string | number | null][] = [
  ['Business Name', (l) => l.business_name],
  ['Business Type', (l) => l.business_type],
  ['Address', (l) => l.address],
  ['Neighborhood', (l) => l.neighborhood],
  ['Owner Name', (l) => l.owner_name],
  ['Owner Role', (l) => l.owner_role],
  ['Owner Language', (l) => l.owner_language],
  ['Phone', (l) => l.phone],
  ['Email', (l) => l.email],
  ['Instagram', (l) => l.instagram],
  ['Current POS', (l) => l.current_pos],
  ['POS Monthly Cost', (l) => l.pos_monthly_cost],
  ['Contract End Date', (l) => l.contract_end_date],
  ['ETF Amount', (l) => l.etf_amount],
  ['Stage', (l) => l.stage],
  ['Heat', (l) => l.heat],
  ['Best Visit Window', (l) => l.best_visit_window],
  ['Notes', (l) => l.notes],
  ['Google Place ID', (l) => l.place_id],
  ['Created At', (l) => l.created_at],
  ['Updated At', (l) => l.updated_at],
  ['Lead ID', (l) => l.id],
];

export const leadsCsv = (leads: readonly Lead[]): string =>
  BOM +
  buildCsv(
    LEAD_COLUMNS.map(([h]) => h),
    leads.map((l) => LEAD_COLUMNS.map(([, fn]) => fn(l))),
  );

const VISIT_COLUMNS: readonly [string, (v: Visit, businessName: string) => string | number | null][] = [
  ['Business Name', (_, name) => name],
  ['Visited At', (v) => v.visited_at],
  ['Spoke With', (v) => v.spoke_with],
  ['Outcome', (v) => v.outcome],
  ['Notes', (v) => v.notes],
  ['Next Action', (v) => v.next_action],
  ['Next Action Date', (v) => v.next_action_date],
  ['Next Action Time', (v) => v.next_action_time],
  ['Lead ID', (v) => v.lead_id],
  ['Visit ID', (v) => v.id],
];

export const visitsCsv = (visits: readonly Visit[], leads: readonly Lead[]): string => {
  const nameById = new Map(leads.map((l) => [l.id, l.business_name]));
  const sorted = [...visits].sort((a, b) =>
    a.visited_at < b.visited_at ? 1 : a.visited_at > b.visited_at ? -1 : 0,
  );
  return (
    BOM +
    buildCsv(
      VISIT_COLUMNS.map(([h]) => h),
      sorted.map((v) => VISIT_COLUMNS.map(([, fn]) => fn(v, nameById.get(v.lead_id) ?? '(deleted lead)'))),
    )
  );
};
